import type { AppConfig } from '../config'

/** Set when @sentry/react is loaded (dynamic import). Missing in dev when package not installed. */
let Sentry: typeof import('@sentry/react') | null = null
let initialized = false
let initPromise: Promise<void> | null = null

/**
 * Initialize Sentry with runtime config.
 * Called from ConfigLoader after config.json is loaded.
 * Safe to call multiple times -- only initializes once.
 * No-ops if @sentry/react is not installed (e.g. dev Docker without Sentry deps).
 */
export function initSentry(config: AppConfig): void {
  if (initialized || !config.sentryDsn) return
  if (initPromise) return
  initPromise = (async () => {
    try {
      Sentry = await import('@sentry/react')
    } catch {
      Sentry = null
      return
    }
    if (!Sentry) return
    Sentry.init({
      dsn: config.sentryDsn,
      environment: config.environment,
      release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
      ],
      tracesSampleRate: config.environment === 'production' ? 0.1 : 1.0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      tracePropagationTargets: ['localhost', /^https:\/\/.*\.bizcontent\.ai/],
      denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
      ignoreErrors: [
        'Network Error',
        'Failed to fetch',
        'Load failed',
        'ChunkLoadError',
        'ResizeObserver loop',
        'Non-Error promise rejection',
        'session has expired',
        'sesión ha expirado',
        'session a expiré',
        'Token has expired',
        'Not authenticated',
        'Invalid or expired verification token',
      ],
      beforeSend(event) {
        const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
        const redact = (s: string) => s.replace(emailRegex, '***@redacted')
        if (event.message) event.message = redact(event.message)
        if (event.exception?.values) {
          for (const ex of event.exception.values) {
            if (ex.value) ex.value = redact(ex.value)
          }
        }
        return event
      },
    })
    initialized = true
  })()
}

/**
 * Set the current user in Sentry context.
 * Call after login/auth state change.
 */
export function setSentryUser(user: { id: number; role?: string } | null): void {
  if (!initialized || !Sentry) return
  Sentry.setUser(user ? { id: String(user.id), role: user.role } : null)
}

/**
 * Capture an exception in Sentry with optional context.
 */
export function captureException(error: unknown, context?: string): void {
  if (!initialized || !Sentry) return
  Sentry.captureException(error, { tags: context ? { context } : undefined })
}

/**
 * Capture a message in Sentry.
 */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
  if (!initialized || !Sentry) return
  Sentry.captureMessage(message, level)
}
